import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useFetcher } from '@remix-run/react';
import type { CSSProperties } from 'styled-components';

import Button from '~/components/Button';
import StatusInputs from '~/components/Forms/StatusInputs';
import useToast from '~/hooks/useToast';

interface Props {
  postId: string;
  commentId?: string;
  commenterId?: string | number;
  subscriptionId?: number;
  userId?: string;
  onDelete: () => void;
  isLoggedInAsAdmin: boolean;
  buttonBackgroundColor?: CSSProperties['backgroundColor'];
  buttonTextColor?: CSSProperties['color'];
}

const DeleteAction = ({
  postId,
  commentId,
  commenterId,
  subscriptionId,
  onDelete,
  userId,
  isLoggedInAsAdmin = false,
  buttonBackgroundColor,
  buttonTextColor,
}: Props) => {
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);

  const fetcher = useFetcher<{ toast: any }>();
  const isSubmitting = fetcher.state === 'submitting';
  const fetcherData = fetcher.data;

  useToast(fetcherData?.toast);

  useEffect(() => {
    if (fetcherData?.toast) {
      onDelete();
    }
  }, [fetcherData?.toast, onDelete]);

  const isRemoving = isLoggedInAsAdmin && commenterId !== userId;
  const ctaText = isRemoving ? t('comments.removing.remove') : t('comments.deleting.delete');
  const ctaSubmittingText = isRemoving ? t('comments.removing.removing') : t('comments.deleting.deleting');

  return (
    <div className="flex items-start space-x-4">
      <div className="min-w-0 flex-1">
        <fetcher.Form
          ref={formRef}
          method="post"
          action={isLoggedInAsAdmin ? '/admins/delete_comment' : '/delete_comment'}
          className="relative"
        >
          <label htmlFor="comment" className="sr-only">
            {t('comments.deleting.delete_comment')}
          </label>
          <input type="hidden" name="post_id" value={postId} />
          <input type="hidden" name="comment_id" value={commentId} />
          <input type="hidden" name="subscription_id" value={subscriptionId} />
          <input type="hidden" name="user_id" value={userId} />
          <StatusInputs
            name="delete_comment"
            errorMessage={t('errors.default')}
            successMessage={t('comments.deleting.deleted')}
          />

          <div className="flex justify-end pr-1">
            <Button
              type="submit"
              btnSize="md"
              disabled={isSubmitting}
              style={{
                backgroundColor: buttonBackgroundColor,
                color: buttonTextColor,
              }}
            >
              {isSubmitting ? ctaSubmittingText : ctaText}
            </Button>
          </div>
        </fetcher.Form>
      </div>
    </div>
  );
};

export default DeleteAction;
